import React, { useRef, useState } from 'react';
import { Upload, Image as ImageIcon } from 'lucide-react';

interface ImageUploaderProps {
  onImageSelected: (base64: string, mimeType: string, previewUrl: string) => void;
  className?: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ onImageSelected, className = '' }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please upload a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64Content = result.split(',')[1];
      const mimeType = file.type;
      onImageSelected(base64Content, mimeType, result);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  return (
    <div
      onClick={() => fileInputRef.current?.click()}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={`
        relative group cursor-pointer w-full border-2 border-dashed rounded-2xl flex flex-col items-center justify-center transition-all duration-300
        ${isDragging 
          ? 'border-brand-500 bg-brand-500/10 scale-[1.02]' 
          : 'border-slate-600 hover:border-brand-400 hover:bg-slate-800/50 bg-slate-800/20'
        }
        ${className || 'h-64'} 
      `}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={(e) => e.target.files && handleFile(e.target.files[0])}
        accept="image/*"
        className="hidden"
      />
      
      <div className="flex flex-col items-center p-6 text-center space-y-4">
        <div className={`
          p-4 rounded-full transition-colors duration-300
          ${isDragging ? 'bg-brand-500 text-white' : 'bg-slate-700 text-slate-300 group-hover:bg-brand-500/20 group-hover:text-brand-400'}
        `}>
          {isDragging ? <Upload size={32} /> : <ImageIcon size={32} />}
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">
            Upload Image
          </h3>
          <p className="text-sm text-slate-400">
            Drag & drop or click
          </p>
        </div>
        <div className="text-xs text-slate-500">
          JPG, PNG, WEBP
        </div>
      </div>
    </div>
  );
};